import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { ILastEnrollmentContract } from "@features/courses/ui/last-enrollment";
import { IMetadataContract } from "@features/student/metadata/model";
import { IStreakContract } from "@features/streak/model";
import { ReEngagementApi } from "@features/shared/api/re-engagement.api";
import { SubscriptionApi } from "@features/shared/api/subscription";
import { StreakApi } from "@features/shared/api/streak.api";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { AnalyticsService } from "@features/shared/analytics";

type SkillPayload = {
    skill_type: string;
    level: number;
};

type QuizPayload = {
    round: number;
    is_correct: boolean;
};

type TrackPayload = {
    eventName: string;
    props?: Record<string, string | number | boolean | null | undefined>;
};

type ReEngagementPlanType = string | null;

const MAX_STEP = 9;
const QUIZ_ROUNDS_COUNT = 3;
const RE_ENGAGEMENT_EVENT_PREFIX = "re_engagement_";
const MS_IN_DAY = 24 * 60 * 60 * 1000;
const RE_ENGAGEMENT_STEP_STORAGE_KEY = "re_engagement_current_step";

const toNullableNumber = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value.trim());
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const mapPeriodTypeToPlanType = (periodType: unknown): ReEngagementPlanType => {
    if (typeof periodType !== "string") return null;
    const normalizedPeriodType = periodType.trim().toUpperCase();
    if (!normalizedPeriodType) return null;

    switch (normalizedPeriodType) {
        case "W":
            return "week";
        case "Q":
            return "quarter";
        case "M":
            return "month";
        case "Y":
            return "year";
        default:
            return periodType;
    }
};

const getDaysFromTimestamp = (timestamp: unknown): number | null => {
    if (typeof timestamp !== "string") return null;
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return null;

    return Math.max(0, Math.floor((Date.now() - parsed) / MS_IN_DAY));
};

const getReEngagementCommonProps = (
    metadata: Record<string, any> | null | undefined,
    fallbackPlanType: ReEngagementPlanType
) => {
    const planType = fallbackPlanType;
    const daysInactive =
        toNullableNumber(metadata?.days_inactive) ??
        getDaysFromTimestamp(metadata?.last_lesson_activity_at) ??
        getDaysFromTimestamp(metadata?.continue_learning_modal_last_continue_clicked_at);

    return {
        plan_type: planType,
        days_inactive: daysInactive
    };
};

const getStoredReEngagementStep = (): number => {
    if (typeof window === "undefined") return 1;

    const raw = window.sessionStorage.getItem(RE_ENGAGEMENT_STEP_STORAGE_KEY);
    const parsed = Number(raw);
    if (!Number.isInteger(parsed)) return 1;
    return Math.min(MAX_STEP, Math.max(1, parsed));
};

const storesReset = createEvent();
const nextStep = createEvent();
const prevStep = createEvent();
const toolsetSkipped = createEvent();
const wizardCompleted = createEvent();
const goalSelected = createEvent<string>();
const skillChanged = createEvent<SkillPayload>();
const paceSelected = createEvent<string>();
const quizAnswered = createEvent<QuizPayload>();
const quizClosed = createEvent();
const quizCompleted = createEvent();
const giftClaimed = createEvent();
const giftSkipped = createEvent();
const wizardVisibilityChanged = createEvent<boolean>();
const trackRequested = createEvent<TrackPayload>();
const restoreStepFromSession = createEvent();

const claimGiftFx = createEffect(async () => {
    return await ReEngagementApi.claimGift();
});

const refreshStreakStatsSilentFx = createEffect(async () => {
    return await StreakApi.getStreakStats();
});

const IS_PROD = process.env.NEXT_PUBLIC_MODE === "production";
const RE_ENGAGEMENT_QUIZ_TOPIC_ID = IS_PROD ? 241 : 83;
const RE_ENGAGEMENT_QUIZ_LESSON_ID = IS_PROD ? 1024 : 460;

const completeLessonForStreakFx = createEffect(async () => {
    await CourseDataApi.completeLesson({ topicId: RE_ENGAGEMENT_QUIZ_TOPIC_ID, lessonId: RE_ENGAGEMENT_QUIZ_LESSON_ID });
});

const fetchCurrentPlanTypeFx = createEffect(async (): Promise<ReEngagementPlanType> => {
    try {
        const subscriptions = (await SubscriptionApi.getSubscriptions()) as Array<Record<string, any>>;
        return mapPeriodTypeToPlanType(subscriptions[0]?.plan?.periodType);
    } catch {
        return null;
    }
});

const trackEventFx = createEffect(({ eventName, props }: TrackPayload) => {
    AnalyticsService.trackEvent(eventName, props ?? {});
});
const persistStepToSessionFx = createEffect((step: number) => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(RE_ENGAGEMENT_STEP_STORAGE_KEY, String(step));
});
const clearPersistedStepFx = createEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(RE_ENGAGEMENT_STEP_STORAGE_KEY);
});

const $reEngagementStep = createStore<number>(1);
const $reEngagementGoal = createStore<string | null>(null).on(goalSelected, (_, goal) => goal);
const $reEngagementSkill = createStore<SkillPayload | null>(null).on(skillChanged, (_, payload) => payload);
const $reEngagementPace = createStore<string | null>(null).on(paceSelected, (_, pace) => pace);
const $reEngagementQuizAnswers = createStore<QuizPayload[]>([]).on(quizAnswered, (state, payload) => [...state, payload]);
const $reEngagementGiftAvailable = createStore<boolean>(true);
const $reEngagementCurrentPlanType = createStore<ReEngagementPlanType>(null).on(
    fetchCurrentPlanTypeFx.doneData,
    (_, value) => value
);

const stepContinueClickEventMap: Record<number, string> = {
    1: "re_engagement_welcome_continue_clicked",
    2: "re_engagement_reassurance_continue_clicked",
    3: "re_engagement_toolset_continue_clicked",
    4: "re_engagement_goal_confirm_clicked",
    5: "re_engagement_skill_continue_clicked",
    6: "re_engagement_pace_continue_clicked",
    7: "re_engagement_quiz_continue_clicked"
};

const stepPresentedEventMap: Record<number, string> = {
    1: "re_engagement_welcome_screen_presented",
    2: "re_engagement_reassurance_screen_presented",
    3: "re_engagement_toolset_screen_presented",
    4: "re_engagement_goal_screen_presented",
    5: "re_engagement_skill_screen_presented",
    6: "re_engagement_pace_screen_presented",
    7: "re_engagement_quiz_screen_presented",
    8: "re_engagement_gift_screen_presented",
    9: "re_engagement_streak_screen_presented"
};

sample({
    clock: nextStep,
    source: $reEngagementStep,
    filter: (step) => Boolean(stepContinueClickEventMap[step]),
    fn: (step) => ({ eventName: stepContinueClickEventMap[step] }),
    target: trackRequested
});

sample({
    clock: nextStep,
    source: $reEngagementStep,
    fn: (step) => Math.min(step + 1, MAX_STEP),
    target: $reEngagementStep
});

sample({
    clock: quizClosed,
    source: $reEngagementStep,
    fn: (step) => Math.min(step + 1, MAX_STEP),
    target: $reEngagementStep
});

sample({
    clock: restoreStepFromSession,
    fn: () => getStoredReEngagementStep(),
    target: $reEngagementStep
});

sample({
    clock: prevStep,
    source: $reEngagementStep,
    fn: (step) => Math.max(step - 1, 1),
    target: $reEngagementStep
});

sample({
    clock: toolsetSkipped,
    fn: () => ({ eventName: "re_engagement_toolset_skip_clicked" }),
    target: trackRequested
});

sample({
    clock: giftSkipped,
    target: nextStep
});

sample({
    clock: giftClaimed,
    fn: () => ({ eventName: "re_engagement_gift_claimed" }),
    target: trackRequested
});

sample({
    clock: giftClaimed,
    target: claimGiftFx
});

sample({
    clock: claimGiftFx.done,
    target: nextStep
});

sample({
    clock: goalSelected,
    fn: (goal) => ({ eventName: "re_engagement_goal_selected", props: { goal } }),
    target: trackRequested
});

sample({
    clock: skillChanged,
    fn: ({ skill_type, level }) => ({ eventName: "re_engagement_skill_changed", props: { skill_type, level } }),
    target: trackRequested
});

sample({
    clock: paceSelected,
    fn: (pace) => ({ eventName: "re_engagement_pace_selected", props: { pace } }),
    target: trackRequested
});

sample({
    clock: quizAnswered,
    fn: ({ round, is_correct }) => ({ eventName: "re_engagement_quiz_answered", props: { round, is_correct } }),
    target: trackRequested
});

sample({
    clock: quizClosed,
    fn: () => ({ eventName: "re_engagement_quiz_close_clicked" }),
    target: trackRequested
});

sample({
    clock: quizAnswered,
    source: $reEngagementQuizAnswers,
    filter: (answers) => answers.length === QUIZ_ROUNDS_COUNT,
    target: quizCompleted
});

sample({
    clock: wizardCompleted,
    fn: () => ({ eventName: "re_engagement_streak_continue_clicked" }),
    target: trackRequested
});

const contract = new ModelContract(
    {
        $reEngagementStep,
        $reEngagementGoal,
        $reEngagementSkill,
        $reEngagementPace,
        $reEngagementQuizAnswers,
        $reEngagementGiftAvailable
    },
    {
        storesReset,
        nextStep,
        prevStep,
        toolsetSkipped,
        wizardCompleted,
        goalSelected,
        skillChanged,
        paceSelected,
        quizAnswered,
        quizClosed,
        giftClaimed,
        giftSkipped
    },
    { claimGiftFx },
    {}
);

export type IReEngagementContract = typeof contract;

export class ReEngagementModule extends ModuleDescription<IReEngagementContract> {
    constructor(
        public lastEnrollmentModule: ILastEnrollmentContract,
        public metadataModule: IMetadataContract,
        public streakModule: IStreakContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            source: {
                metadata: this.metadataModule.stores.$metadata,
                currentPlanType: $reEngagementCurrentPlanType
            },
            clock: trackRequested,
            fn: ({ metadata, currentPlanType }, payload) => {
                if (!payload.eventName.startsWith(RE_ENGAGEMENT_EVENT_PREFIX)) {
                    return payload;
                }

                return {
                    ...payload,
                    props: {
                        ...getReEngagementCommonProps(metadata, currentPlanType),
                        ...(payload.props ?? {})
                    }
                };
            },
            target: trackEventFx
        });

        sample({
            clock: this.lastEnrollmentModule.stores.$isReEngagementWizardVisible,
            target: wizardVisibilityChanged
        });

        sample({
            clock: wizardVisibilityChanged,
            filter: (isVisible) => !isVisible,
            target: storesReset
        });

        sample({
            clock: wizardVisibilityChanged,
            filter: (isVisible) => isVisible,
            target: restoreStepFromSession
        });

        sample({
            clock: wizardVisibilityChanged,
            filter: (isVisible) => isVisible,
            target: fetchCurrentPlanTypeFx
        });

        sample({
            clock: claimGiftFx.done,
            target: this.metadataModule.events.refreshMetadataRequested
        });

        sample({
            clock: quizCompleted,
            target: completeLessonForStreakFx
        });

        sample({
            clock: [completeLessonForStreakFx.done, completeLessonForStreakFx.fail],
            target: [refreshStreakStatsSilentFx, this.streakModule.effects.getStreakListFx]
        });

        sample({
            clock: refreshStreakStatsSilentFx.doneData,
            target: this.streakModule.events.statsChanged
        });

        sample({
            source: this.metadataModule.stores.$metadata,
            fn: (metadata) => metadata.re_engagement_gift_claimed !== "true",
            target: $reEngagementGiftAvailable
        });

        sample({
            clock: wizardVisibilityChanged,
            filter: (isVisible) => isVisible,
            fn: () => {
                const step = getStoredReEngagementStep();
                return { eventName: stepPresentedEventMap[step] };
            },
            target: trackRequested
        });

        sample({
            clock: $reEngagementStep.updates,
            source: this.lastEnrollmentModule.stores.$isReEngagementWizardVisible,
            filter: (isVisible, step) => isVisible && Boolean(stepPresentedEventMap[step]),
            fn: (_, step) => ({ eventName: stepPresentedEventMap[step] }),
            target: trackRequested
        });

        sample({
            clock: wizardCompleted,
            target: this.lastEnrollmentModule.events.reEngagementFlowCompleted
        });

        sample({
            clock: toolsetSkipped,
            target: this.lastEnrollmentModule.events.reEngagementFlowCompleted
        });

        reset({
            clock: [storesReset, this.lastEnrollmentModule.events.reEngagementFlowCompleted],
            target: [$reEngagementStep, $reEngagementGoal, $reEngagementSkill, $reEngagementPace, $reEngagementQuizAnswers]
        });

        sample({
            clock: $reEngagementStep.updates,
            target: persistStepToSessionFx
        });

        sample({
            clock: [storesReset, this.lastEnrollmentModule.events.reEngagementFlowCompleted],
            target: clearPersistedStepFx
        });
    }
}
